import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Car, Mail, Lock, User, Building, Phone, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const API = '/api';

export default function AdvertiserLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({
    company_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    password: "",
    confirmPassword: ""
  });

  // Check for magic link token
  const magicToken = searchParams.get("token");
  if (magicToken) {
    // Handle magic link login
    handleMagicLogin(magicToken);
  }

  async function handleMagicLogin(token) {
    try {
      await axios.post(`${API}/advertiser-portal/magic-login?token=${token}`, {}, { withCredentials: true });
      toast.success("Login successful!");
      navigate("/advertiser");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid or expired link");
    }
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/advertiser-portal/login`, loginData, { withCredentials: true });
      toast.success("Login successful!");
      navigate("/advertiser");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.password !== registerData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (registerData.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/advertiser-portal/register`, {
        company_name: registerData.company_name,
        contact_name: registerData.contact_name,
        contact_email: registerData.contact_email,
        contact_phone: registerData.contact_phone,
        password: registerData.password
      }, { withCredentials: true });
      toast.success("Registration successful!");
      navigate("/advertiser");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLinkRequest = async () => {
    if (!loginData.email) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/advertiser-portal/magic-link`, { email: loginData.email });
      setMagicLinkSent(true);
      toast.success("Check your email for the login link!");
      // Dev mode: show the link
      if (response.data.debug_link) {
        console.log("Magic link:", response.data.debug_link);
      }
    } catch (error) {
      toast.error("Failed to send magic link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="advertiser-login">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              ParkWise
            </span>
          </div>
          <h1 className="text-xl text-white font-semibold">Advertiser Portal</h1>
          <p className="text-slate-400 text-sm mt-1">Manage your campaigns and reach customers</p>
        </div>

        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-6">
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-slate-800">
                <TabsTrigger value="login">Sign In</TabsTrigger>
                <TabsTrigger value="register">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="space-y-4 mt-6">
                {magicLinkSent ? (
                  <div className="text-center py-8">
                    <Mail className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                    <h3 className="text-white font-semibold mb-2">Check your email</h3>
                    <p className="text-slate-400 text-sm">We've sent a magic link to {loginData.email}</p>
                    <Button 
                      variant="link" 
                      className="text-blue-400 mt-4"
                      onClick={() => setMagicLinkSent(false)}
                    >
                      Use password instead
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label className="text-slate-300">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input
                          type="email"
                          placeholder="you@company.com"
                          value={loginData.email}
                          onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                          className="pl-10 bg-slate-800 border-slate-700 text-white"
                          required
                          data-testid="login-email"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input
                          type="password"
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                          className="pl-10 bg-slate-800 border-slate-700 text-white"
                          required
                          data-testid="login-password"
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      disabled={loading}
                      data-testid="login-submit"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Sign In
                    </Button>
                    <div className="text-center">
                      <Button 
                        type="button"
                        variant="link" 
                        className="text-slate-400 text-sm"
                        onClick={handleMagicLinkRequest}
                        disabled={loading}
                      >
                        Send me a magic link instead
                      </Button>
                    </div>
                  </form>
                )}
              </TabsContent>

              <TabsContent value="register" className="space-y-4 mt-6">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Company Name</Label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        placeholder="Acme Corporation"
                        value={registerData.company_name}
                        onChange={(e) => setRegisterData({...registerData, company_name: e.target.value})}
                        className="pl-10 bg-slate-800 border-slate-700 text-white"
                        required
                        data-testid="register-company"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Your Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input
                          placeholder="John Smith"
                          value={registerData.contact_name}
                          onChange={(e) => setRegisterData({...registerData, contact_name: e.target.value})}
                          className="pl-10 bg-slate-800 border-slate-700 text-white"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300">Phone</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input
                          placeholder="07xxx"
                          value={registerData.contact_phone}
                          onChange={(e) => setRegisterData({...registerData, contact_phone: e.target.value})}
                          className="pl-10 bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-300">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        type="email"
                        placeholder="you@company.com"
                        value={registerData.contact_email}
                        onChange={(e) => setRegisterData({...registerData, contact_email: e.target.value})}
                        className="pl-10 bg-slate-800 border-slate-700 text-white"
                        required
                        data-testid="register-email"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Password</Label>
                      <Input
                        type="password"
                        placeholder="Min 8 chars"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white"
                        required
                        data-testid="register-password"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Confirm</Label>
                      <Input
                        type="password"
                        placeholder="Confirm"
                        value={registerData.confirmPassword}
                        onChange={(e) => setRegisterData({...registerData, confirmPassword: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white"
                        required
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    disabled={loading}
                    data-testid="register-submit"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Create Account
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-sm mt-6">
          By continuing, you agree to our Terms of Service
        </p>
      </div>
    </div>
  );
}
